import axios from "axios";
import { getAuthHeader } from "./utils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/**
 * Fetch upcoming elections from the Democracy Works API via our backend proxy.
 * The backend reads the user's address from the DB and forwards the request
 * to DW server-side, keeping the API key private.
 *
 * @returns {Promise<Array>} Array of DW election objects, or [] on error.
 */
export const fetchDWElections = async () => {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(`${API_BASE_URL}/api/dw/elections/`, {
      headers,
      timeout: 20000,
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error(
      "Error fetching Democracy Works elections:",
      error.response?.data || error.message
    );
    return [];
  }
};

/**
 * Public, unauthenticated proxy to DW /v2/elections.
 * Accepts either a full address (street/city/state/zip) for exact ballot
 * results or a state code for broad statewide results.
 *
 * @param {Object} opts
 * @param {string} [opts.street]
 * @param {string} [opts.city]
 * @param {string} [opts.state]   2-letter state code (e.g. "CA")
 * @param {string} [opts.zip]
 * @returns {Promise<{elections: Array, precision: string|null, source: string}>}
 */
export const fetchPublicDWElections = async ({ street, city, state, zip } = {}) => {
  try {
    const params = {};
    if (street) params.street = street;
    if (city) params.city = city;
    if (state) params.state = state;
    if (zip) params.zip = zip;
    const response = await axios.get(`${API_BASE_URL}/api/public/dw/elections/`, {
      params,
      timeout: 20000,
    });
    const data = response?.data || {};
    return {
      elections: Array.isArray(data.elections) ? data.elections : [],
      precision: data.precision || null,
      source: data.source || "Democracy Works",
    };
  } catch (error) {
    console.error(
      "Error fetching public Democracy Works elections:",
      error.response?.data || error.message
    );
    return { elections: [], precision: null, source: "Democracy Works", error: true };
  }
};

/**
 * Convenience wrapper for state-only public elections lookups.
 */
export const fetchPublicDWStateElections = (stateCode) =>
  fetchPublicDWElections({ state: stateCode });

/**
 * Public, unauthenticated proxy to DW /v2/authorities/state/{stateCode}.
 * Returns the unwrapped authority object (or null on error).
 */
export const fetchPublicDWStateAuthority = async (stateCode) => {
  if (!stateCode) return null;
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/public/dw/authority/${String(stateCode).toUpperCase()}/`,
      { timeout: 20000 }
    );
    return response?.data?.authority || null;
  } catch (error) {
    console.error(
      "Error fetching public Democracy Works state authority:",
      error.response?.data || error.message
    );
    return null;
  }
};
